import { useMemo } from 'react';
import { FuelTypes } from '../../../../../domain/vehicle/Vehicle';
import { roundToTwoDecimals } from '../../../../../helpers/Units';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActivePlan } from '../plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../plan-details/useRetrieveActivePlanDrivers';

const EmissionsPerLiter: Record<FuelTypes, number> = {
  diesel: 2.68,
  gasoline: 2.31,
  electric: 0,
};

export interface UseRetrieveAnalyticsPlanEmissionsResponse {
  totalEmissions: number;
  averageRouteEmissions: number;
  averageStopEmissions: number;
  vehiclesWithoutEmissions: number;
}

export const useRetrieveAnalyticsPlanEmissions = (): UseRetrieveAnalyticsPlanEmissionsResponse => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();
  const { data: activePlan } = useRetrieveActivePlan();

  const { totalEmissions, averageRouteEmissions, averageStopEmissions, vehiclesWithoutEmissions } =
    useMemo<UseRetrieveAnalyticsPlanEmissionsResponse>(() => {
      let totalEmissions = 0;
      let averageRouteEmissions = 0;
      let averageStopEmissions = 0;
      let vehiclesWithoutEmissions = 0;

      if (!activePlan || !activeProject || !planDrivers) {
        return {
          totalEmissions,
          averageRouteEmissions,
          averageStopEmissions,
          vehiclesWithoutEmissions,
        };
      }

      totalEmissions = roundToTwoDecimals(
        planDrivers.reduce((prev, driver) => {
          if (!driver.fuel_type || !driver.consumption) {
            return prev;
          }
          const distance = driver.getTotalDistanceKM();
          const fuelConsumption = driver.consumption;
          const emissionsPerLiter = EmissionsPerLiter[driver.fuel_type];
          return prev + (distance / 100) * fuelConsumption * emissionsPerLiter;
        }, 0)
      );

      averageRouteEmissions = roundToTwoDecimals(totalEmissions / (planDrivers.length || 1));

      averageStopEmissions = roundToTwoDecimals(totalEmissions / (activePlan.total_stops || 1));

      vehiclesWithoutEmissions = planDrivers.filter((driver) => !driver.fuel_type || !driver.consumption).length;

      return {
        totalEmissions,
        averageRouteEmissions,
        averageStopEmissions,
        vehiclesWithoutEmissions,
      };
    }, [activePlan, planDrivers, activeProject]);

  return {
    totalEmissions,
    averageRouteEmissions,
    averageStopEmissions,
    vehiclesWithoutEmissions,
  };
};
